import { useTranslation } from "react-i18next";
import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Textarea } from "../components/ui/textarea";
import { Zap, Plus, Trash2, Pencil, Check, X, ChevronRight, History, AlertCircle, CheckCircle2, Braces } from "lucide-react";
import { useAppearance } from "../contexts/AppearanceContext";
import { toast } from "../components/ui/sonner";
import DeleteConfirmDialog from "../components/DeleteConfirmDialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Modules available for trigger and conditions
const MODULES = [
  { value: "negocios",  label: "Negócios" },
  { value: "contatos",  label: "Contatos" },
  { value: "empresas",  label: "Empresas" },
  { value: "produtos",  label: "Produtos" },
  { value: "tarefas",   label: "Tarefas" },
];

// Trigger events per module (labels without module prefix)
const TRIGGERS_BY_MODULE = {
  negocios: [
    { value: "negocio_criado",           label: "Criado" },
    { value: "negocio_editado",          label: "Editado" },
    { value: "negocio_movido_etapa",     label: "Movido de etapa" },
    { value: "negocio_ganho",            label: "Ganho" },
    { value: "negocio_perdido",          label: "Perdido" },
    { value: "negocio_ganho_ou_perdido", label: "Ganho ou perdido" },
    { value: "sla_prazo_excedido",       label: "Atingir o limite de tempo na etapa" },
    { value: "sla_inatividade_excedida", label: "Atingir o limite de tempo sem movimentação" },
  ],
  contatos: [
    { value: "contato_criado",  label: "Criado" },
    { value: "contato_editado", label: "Editado" },
  ],
  empresas: [
    { value: "empresa_criada",  label: "Criada" },
    { value: "empresa_editada", label: "Editada" },
  ],
  produtos: [
    { value: "produto_criado",  label: "Criado" },
    { value: "produto_editado", label: "Editado" },
  ],
  tarefas: [
    { value: "tarefa_criada",    label: "Criada" },
    { value: "tarefa_editada",   label: "Editada" },
    { value: "tarefa_concluida", label: "Concluída" },
    { value: "tarefa_vencida",   label: "Vencida" },
    { value: "tarefa_cancelada", label: "Cancelada" },
  ],
};

// System fields per module (sorted alphabetically by label)
const SYSTEM_FIELDS_BY_MODULE = {
  negocios: [
    { field_name: "contato_id",     field_label: "Contato",       value_type: "contato" },
    { field_name: "empresa_id",     field_label: "Empresa",       value_type: "empresa" },
    { field_name: "estagio_id",     field_label: "Etapa",         value_type: "estagio" },
    { field_name: "pipeline_id",    field_label: "Pipeline",      value_type: "pipeline" },
    { field_name: "responsavel_id", field_label: "Responsável",   value_type: "responsavel" },
    { field_name: "status",         field_label: "Status",        value_type: "status_negocio" },
    { field_name: "titulo",         field_label: "Título" },
  ],
  contatos: [
    { field_name: "empresa_id", field_label: "Empresa", value_type: "empresa" },
    { field_name: "nome",       field_label: "Nome" },
  ],
  empresas: [
    { field_name: "nome", field_label: "Nome" },
  ],
  produtos: [
    { field_name: "nome",        field_label: "Nome" },
    { field_name: "preco_venda", field_label: "Preço de Venda" },
    { field_name: "sku",         field_label: "SKU" },
  ],
  tarefas: [
    { field_name: "data_vencimento", field_label: "Data de Vencimento", value_type: "date_field" },
    { field_name: "prioridade",      field_label: "Prioridade",    value_type: "prioridade" },
    { field_name: "responsavel_id",  field_label: "Responsável",   value_type: "responsavel" },
    { field_name: "status",          field_label: "Status",        value_type: "status_tarefa" },
    { field_name: "tipo",            field_label: "Tipo",          value_type: "tipo_tarefa" },
    { field_name: "titulo",          field_label: "Título" },
  ],
};

// Static options for specific value types
const STATIC_VALUE_OPTIONS = {
  status_negocio: [
    { value: "aberto",  label: "Aberto" },
    { value: "ganho",   label: "Ganho" },
    { value: "perdido", label: "Perdido" },
  ],
  status_tarefa: [
    { value: "aberta",    label: "Aberta" },
    { value: "concluida", label: "Concluída" },
    { value: "cancelada", label: "Cancelada" },
  ],
  prioridade: [
    { value: "baixa",   label: "Baixa" },
    { value: "media",   label: "Média" },
    { value: "alta",    label: "Alta" },
    { value: "urgente", label: "Urgente" },
  ],
};

// Helper to get full trigger label including module name
function getTriggerFullLabel(event, module) {
  const modLabel = MODULES.find(m => m.value === module)?.label || module;
  const evLabel = (TRIGGERS_BY_MODULE[module] || []).find(opt => opt.value === event)?.label || event;
  return `${modLabel}: ${evLabel}`;
}

const ACTION_TYPES = [
  { value: "rotacionar_responsavel", label: "Rotacionar responsável (round-robin)" },
  { value: "criar_tarefa",           label: "Criar tarefa automática" },
  { value: "encerrar_tarefas",       label: "Encerrar todas as tarefas do negócio" },
  { value: "notificacao_interna",    label: "Enviar notificação interna" },
  { value: "webhook",                label: "Webhook (POST para URL externa)" },
  { value: "enviar_email",           label: "Enviar e-mail" },
  { value: "enviar_whatsapp",        label: "Enviar WhatsApp" },
];

const CONDITION_OPERATORS = [
  { value: "eq",        label: "igual a" },
  { value: "ne",        label: "diferente de" },
  { value: "gt",        label: "maior que" },
  { value: "lt",        label: "menor que" },
  { value: "contains",  label: "contém" },
  { value: "empty",     label: "está vazio" },
  { value: "not_empty", label: "não está vazio" },
];

const RELATIVE_DATE_OPTIONS = [
  { value: "today",          label: "Hoje",                hasN: false },
  { value: "this_week",      label: "Esta semana",          hasN: false },
  { value: "this_month",     label: "Este mês",             hasN: false },
  { value: "last_30_days",   label: "Últimos 30 dias",      hasN: false },
  { value: "last_month",     label: "Mês passado",          hasN: false },
  { value: "this_year",      label: "Este ano",             hasN: false },
  { value: "last_year",      label: "Ano passado",          hasN: false },
  { value: "last_N_days",    label: "Últimos N dias",        hasN: true  },
  { value: "last_N_weeks",   label: "Últimas N semanas",     hasN: true  },
  { value: "last_N_months",  label: "Últimos N meses",       hasN: true  },
  { value: "next_N_days",    label: "Próximos N dias",       hasN: true  },
  { value: "next_N_weeks",   label: "Próximas N semanas",    hasN: true  },
  { value: "next_N_months",  label: "Próximos N meses",      hasN: true  },
];

function parseDateValue(value) {
  // value format: "relative:key" or "relative:key:N" or ISO date string
  if (!value) return { mode: "fixed", relKey: "", relN: "7", fixedDate: "" };
  if (value.startsWith("relative:")) {
    const parts = value.split(":");
    return { mode: "relative", relKey: parts[1] || "", relN: parts[2] || "7", fixedDate: "" };
  }
  return { mode: "fixed", relKey: "", relN: "7", fixedDate: value };
}
function buildDateValue(mode, relKey, relN, fixedDate) {
  if (mode === "relative") {
    const opt = RELATIVE_DATE_OPTIONS.find(o => o.value === relKey);
    return opt?.hasN ? `relative:${relKey}:${relN}` : `relative:${relKey}`;
  }
  return fixedDate;
}

function DateValueInput({ value, onChange }) {
  const { mode, relKey, relN, fixedDate } = parseDateValue(value);
  const selOpt = RELATIVE_DATE_OPTIONS.find(o => o.value === relKey);
  return (
    <div className="flex items-center gap-1.5 flex-1 min-w-0">
      {/* Mode select */}
      <Select value={mode} onValueChange={v => onChange(buildDateValue(v, relKey || "today", relN, fixedDate))}>
        <SelectTrigger className="h-8 text-xs w-24 bg-white flex-shrink-0"><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value="fixed" className="text-xs">Data fixa</SelectItem>
          <SelectItem value="relative" className="text-xs">Relativa</SelectItem>
        </SelectContent>
      </Select>
      {/* Fixed: datepicker */}
      {mode === "fixed" && (
        <Input type="date" value={fixedDate} onChange={e => onChange(e.target.value)} className="h-8 text-xs flex-1 bg-white" />
      )}
      {/* Relative: select + optional N */}
      {mode === "relative" && (
        <>
          <Select value={relKey} onValueChange={v => onChange(buildDateValue("relative", v, relN, fixedDate))}>
            <SelectTrigger className="h-8 text-xs flex-1 min-w-[140px] bg-white"><SelectValue placeholder="Selecionar..." /></SelectTrigger>
            <SelectContent>
              <div className="px-2 py-1 text-[10px] font-bold text-slate-400 uppercase">Fixas</div>
              {RELATIVE_DATE_OPTIONS.filter(o => !o.hasN).map(o => <SelectItem key={o.value} value={o.value} className="text-xs">{o.label}</SelectItem>)}
              <div className="px-2 pt-1 pb-1 text-[10px] font-bold text-slate-400 uppercase border-t mt-1">Com quantidade (N)</div>
              {RELATIVE_DATE_OPTIONS.filter(o => o.hasN).map(o => <SelectItem key={o.value} value={o.value} className="text-xs">{o.label}</SelectItem>)}
            </SelectContent>
          </Select>
          {selOpt?.hasN && (
            <Input type="number" min="1" value={relN}
              onChange={e => onChange(buildDateValue("relative", relKey, e.target.value, fixedDate))}
              className="h-8 text-xs w-14 bg-white flex-shrink-0" placeholder="N" />
          )}
        </>
      )}
    </div>
  );
}

const TEMPLATE_VARS = [
  { key: "titulo",           label: "Título do negócio" },
  { key: "empresa_nome",     label: "Nome da empresa" },
  { key: "contato_nome",     label: "Nome do contato" },
  { key: "produto_nome",     label: "Nome do produto" },
  { key: "responsavel_nome", label: "Nome do responsável" },
  { key: "valor_mrr",        label: "Valor MRR" },
  { key: "valor_setup",      label: "Valor Setup" },
  { key: "status",           label: "Status" },
  { key: "contato_telefone", label: "Telefone do contato" },
  { key: "contato_email",    label: "E-mail do contato" },
];

function VarPicker({ onInsert }) {
  const [open, setOpen] = useState(false);
  const appearance = useAppearance();
  const tc500 = appearance.colors?.["500"] || "#3B82F6";
  const tc50 = appearance.colors?.["50"] || "#EFF6FF";
  return (
    <>
      <button type="button" onClick={() => setOpen(true)}
        className="h-6 px-2 text-[10px] font-semibold rounded border border-slate-300 text-slate-500 hover:bg-slate-50 hover:border-slate-400 transition-colors flex items-center gap-1"
        title="Inserir variável">
        <Braces className="w-3 h-3" />Variáveis
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: tc50 }}>
                <Braces className="w-4 h-4" style={{ color: tc500 }} />
              </div>{"Variáveis disponíveis"}</DialogTitle>
          </DialogHeader>
          <p className="text-xs text-slate-400 -mt-1">Clique para inserir no campo de texto.</p>
          <div className="space-y-1 max-h-72 overflow-y-auto -mx-1 px-1">
            {TEMPLATE_VARS.map(v => (
              <button key={v.key} type="button"
                onClick={() => { onInsert(`{{${v.key}}}`); setOpen(false); }}
                className="w-full flex items-center justify-between gap-4 px-3 py-2.5 rounded-lg hover:bg-slate-50 border border-transparent hover:border-slate-200 transition-all text-left group">
                <span className="text-sm text-slate-700 font-medium whitespace-nowrap">{v.label}</span>
                <code className="text-xs text-slate-500 font-mono bg-slate-100 px-2 py-1 rounded group-hover:bg-white flex-shrink-0">{`{{${v.key}}}`}</code>
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

function ActionEditor({ action, onChange, onRemove, responsaveis, stages, taskTypes }) {
  return (
    <div className="border border-slate-200 rounded-lg p-3 space-y-3 bg-slate-50">
      <div className="flex items-center gap-2">
        <Select value={action.type || ""} onValueChange={v => onChange({ ...action, type: v })}>
          <SelectTrigger className="flex-1 bg-white"><SelectValue placeholder="Selecionar ação..." /></SelectTrigger>
          <SelectContent>{ACTION_TYPES.map(a => <SelectItem key={a.value} value={a.value}>{a.label}</SelectItem>)}</SelectContent>
        </Select>
        <Button type="button" variant="ghost" size="sm" onClick={onRemove} className="text-rose-500 hover:bg-rose-50 h-8 w-8 p-0"><Trash2 className="w-3.5 h-3.5" /></Button>
      </div>

      {action.type === "rotacionar_responsavel" && (
        <div>
          <Label className="text-xs text-slate-600 mb-1 block">Usuários para rotação (round-robin)</Label>
          <div className="space-y-1">
            {(action.user_ids || []).map((uid, i) => {
              const r = responsaveis.find(x => x.user_id === uid);
              return (
                <div key={i} className="flex items-center gap-2 bg-white rounded border border-slate-200 px-2 py-1.5">
                  <span className="text-xs flex-1 text-slate-700">{r?.name || uid}</span>
                  <button type="button" onClick={() => onChange({ ...action, user_ids: action.user_ids.filter((_, j) => j !== i) })} className="text-slate-400 hover:text-rose-500"><X className="w-3 h-3" /></button>
                </div>
              );
            })}
            <Select value="" onValueChange={v => { if (v && !(action.user_ids || []).includes(v)) onChange({ ...action, user_ids: [...(action.user_ids || []), v] }); }}>
              <SelectTrigger className="bg-white text-xs h-8"><SelectValue placeholder="+ Adicionar usuário..." /></SelectTrigger>
              <SelectContent>{responsaveis.map(r => <SelectItem key={r.user_id} value={r.user_id}>{r.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
      )}

      {action.type === "criar_tarefa" && (
        <div className="space-y-2">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Label className="text-xs text-slate-600">Título da tarefa</Label>
              <VarPicker onInsert={v => onChange({ ...action, titulo: (action.titulo || "") + v })} />
            </div>
            <Input value={action.titulo || ""} onChange={e => onChange({ ...action, titulo: e.target.value })} placeholder="Ex: Follow-up com {{titulo}}" className="h-8 text-sm" /></div>
          <div className="grid grid-cols-3 gap-2">
            <div><Label className="text-xs text-slate-600">Tipo</Label>
              <Select value={action.tipo || ""} onValueChange={v => onChange({ ...action, tipo: v })}>
                <SelectTrigger className="h-8 text-xs mt-1"><SelectValue placeholder="Tipo..." /></SelectTrigger>
                <SelectContent>{taskTypes.map(tt => <SelectItem key={tt.id} value={tt.name}>{tt.name}</SelectItem>)}</SelectContent>
              </Select></div>
            <div><Label className="text-xs text-slate-600">Prioridade</Label>
              <Select value={action.prioridade || "media"} onValueChange={v => onChange({ ...action, prioridade: v })}>
                <SelectTrigger className="h-8 text-xs mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="baixa">Baixa</SelectItem><SelectItem value="media">Média</SelectItem>
                  <SelectItem value="alta">Alta</SelectItem><SelectItem value="urgente">Urgente</SelectItem>
                </SelectContent>
              </Select></div>
            <div><Label className="text-xs text-slate-600">Prazo (dias)</Label>
              <Input type="number" min="1" value={action.prazo_dias || 1} onChange={e => onChange({ ...action, prazo_dias: parseInt(e.target.value) || 1 })} className="h-8 text-sm mt-1" /></div>
          </div>
        </div>
      )}

      {action.type === "notificacao_interna" && (
        <div className="space-y-2">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Label className="text-xs text-slate-600">{"Mensagem"}</Label>
              <VarPicker onInsert={v => onChange({ ...action, mensagem: (action.mensagem || "") + v })} />
            </div>
            <Textarea value={action.mensagem || ""} onChange={e => onChange({ ...action, mensagem: e.target.value })} placeholder="Ex: Negócio {{titulo}} foi ganho!" rows={2} className="text-sm" /></div>
          <div><Label className="text-xs text-slate-600">Notificar</Label>
            <Select value={action.para || "responsavel"} onValueChange={v => onChange({ ...action, para: v })}>
              <SelectTrigger className="h-8 text-xs mt-1"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="responsavel">Responsável do negócio</SelectItem><SelectItem value="todos">Todos os usuários</SelectItem></SelectContent>
            </Select></div>
        </div>
      )}

      {action.type === "webhook" && (
        <div><Label className="text-xs text-slate-600">{"URL do Webhook"}</Label>
          <Input value={action.url || ""} onChange={e => onChange({ ...action, url: e.target.value })} placeholder="https://n8n.seusite.com/webhook/..." className="h-8 text-sm mt-1" /></div>
      )}

      {action.type === "enviar_email" && (
        <div className="space-y-2">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Label className="text-xs text-slate-600">Para (e-mail)</Label>
              <VarPicker onInsert={v => onChange({ ...action, para: (action.para || "") + v })} />
            </div>
            <Input value={action.para || ""} onChange={e => onChange({ ...action, para: e.target.value })} placeholder="{{contato_email}} ou fixo@email.com" className="h-8 text-sm" /></div>
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Label className="text-xs text-slate-600">{"Assunto"}</Label>
              <VarPicker onInsert={v => onChange({ ...action, assunto: (action.assunto || "") + v })} />
            </div>
            <Input value={action.assunto || ""} onChange={e => onChange({ ...action, assunto: e.target.value })} placeholder="Negócio {{titulo}} foi ganho!" className="h-8 text-sm" /></div>
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Label className="text-xs text-slate-600">{"Corpo (HTML)"}</Label>
              <VarPicker onInsert={v => onChange({ ...action, corpo: (action.corpo || "") + v })} />
            </div>
            <Textarea value={action.corpo || ""} onChange={e => onChange({ ...action, corpo: e.target.value })} placeholder="<p>Olá, o negócio <strong>{{titulo}}</strong> foi ganho.</p>" rows={3} className="text-sm" /></div>
        </div>
      )}

      {action.type === "enviar_whatsapp" && (
        <div className="space-y-2">
          <div><Label className="text-xs text-slate-600">{"Telefone"}</Label>
            <Input value={action.telefone || ""} onChange={e => onChange({ ...action, telefone: e.target.value })} placeholder="{{contato_telefone}} ou 11999999999" className="h-8 text-sm mt-1" /></div>
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Label className="text-xs text-slate-600">{"Mensagem"}</Label>
              <VarPicker onInsert={v => onChange({ ...action, mensagem: (action.mensagem || "") + v })} />
            </div>
            <Textarea value={action.mensagem || ""} onChange={e => onChange({ ...action, mensagem: e.target.value })} placeholder="Olá! O negócio {{titulo}} foi ganho." rows={2} className="text-sm" /></div>
        </div>
      )}
    </div>
  );
}

export default function AutomacoesPage() {
  const { t } = useTranslation();
  const [automations, setAutomations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editAuto, setEditAuto] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [logsTarget, setLogsTarget] = useState(null);
  const [logs, setLogs] = useState([]);
  const [logsOpen, setLogsOpen] = useState(false);
  const [responsaveis, setResponsaveis] = useState([]);
  const [stages, setStages] = useState([]);
  const [taskTypes, setTaskTypes] = useState([]);
  const [customFields, setCustomFields] = useState([]);
  const [condCustomFields, setCondCustomFields] = useState({}); // { module: [fields] }
  const [dynamicOptions, setDynamicOptions] = useState({}); // { "estagio"|"empresa"|... : [{value,label}] }
  const [form, setForm] = useState({ name: "", trigger: { event: "", module: "" }, conditions: [], actions: [], condition_logic: "AND" });
  const appearance = useAppearance();
  const tc500 = appearance.colors?.["500"] || "#3B82F6";
  const tc50 = appearance.colors?.["50"] || "#EFF6FF";

  const fetchAll = useCallback(async () => {
    try {
      const [autoRes, respRes, ttRes, cfRes] = await Promise.all([
        axios.get(`${API}/automations`, { withCredentials: true }),
        axios.get(`${API}/users/responsaveis`, { withCredentials: true }),
        axios.get(`${API}/task-types`, { withCredentials: true }),
        axios.get(`${API}/custom-fields?module=negocios`, { withCredentials: true }),
      ]);
      setAutomations(autoRes.data);
      setResponsaveis(respRes.data);
      setTaskTypes(ttRes.data);
      setCustomFields(cfRes.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const openNew = () => {
    setEditAuto(null);
    setForm({ name: "Nova automação", trigger: { event: "", module: "" }, conditions: [], actions: [], condition_logic: "AND" });
    setDialogOpen(true);
    loadCondCustomFields("negocios"); // pre-load most common module
  };
  const openEdit = (a) => {
    setEditAuto(a);
    setForm({ name: a.name, trigger: a.trigger || {}, conditions: a.conditions || [], actions: a.actions || [], condition_logic: a.condition_logic || "AND" });
    setDialogOpen(true);
    // Pre-load custom fields for all modules used in this automation
    const mods = new Set(["negocios", a.trigger?.module].filter(Boolean));
    (a.conditions || []).forEach(c => c.module && mods.add(c.module));
    mods.forEach(m => loadCondCustomFields(m));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.trigger?.event) { toast.error("Selecione um trigger"); return; }
    if (form.actions.length === 0) { toast.error("Adicione pelo menos uma ação"); return; }
    try {
      if (editAuto) {
        await axios.put(`${API}/automations/${editAuto.id}`, form, { withCredentials: true });
        toast.success("Automação salva");
      } else {
        await axios.post(`${API}/automations`, form, { withCredentials: true });
        toast.success("Automação criada");
      }
      setDialogOpen(false); fetchAll();
    } catch (err) { toast.error(t("ui.erro_ao_salvar")); }
  };

  const handleToggle = async (auto) => {
    try {
      const res = await axios.patch(`${API}/automations/${auto.id}/toggle`, {}, { withCredentials: true });
      setAutomations(prev => prev.map(a => a.id === auto.id ? { ...a, active: res.data.active } : a));
    } catch (err) { toast.error("Erro ao alterar status"); }
  };

  const openLogs = async (auto) => {
    setLogsTarget(auto);
    try {
      const res = await axios.get(`${API}/automations/${auto.id}/logs`, { withCredentials: true });
      setLogs(res.data);
    } catch { setLogs([]); }
    setLogsOpen(true);
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/automations/${deleteTarget.id}`, { withCredentials: true });
      setDeleteTarget(null); fetchAll();
    } catch (err) { toast.error("Erro ao excluir"); }
  };

  const addCondition = () => {
    const defaultMod = form.trigger?.module || "negocios";
    loadCondCustomFields(defaultMod); // ensure fields are loaded for default module
    setForm(f => ({ ...f, conditions: [...f.conditions, { module: defaultMod, field: "", operator: "eq", value: "" }] }));
  };
  const updateCondition = (i, c) => setForm(f => ({ ...f, conditions: f.conditions.map((x, j) => j === i ? c : x) }));
  const removeCondition = (i) => setForm(f => ({ ...f, conditions: f.conditions.filter((_, j) => j !== i) }));

  const loadCondCustomFields = async (module) => {
    if (condCustomFields[module]) return;
    try {
      const res = await axios.get(`${API}/custom-fields?module=${module}`, { withCredentials: true });
      setCondCustomFields(p => ({ ...p, [module]: res.data }));
    } catch { setCondCustomFields(p => ({ ...p, [module]: [] })); }
  };

  const loadDynamicOptions = async (valueType) => {
    if (dynamicOptions[valueType]) return;
    try {
      let options = [];
      if (valueType === "estagio") {
        // Load all stages from all pipelines
        const pipRes = await axios.get(`${API}/pipelines`, { withCredentials: true });
        const stageArrays = await Promise.all(
          pipRes.data.map(p => axios.get(`${API}/pipeline-stages?pipeline_id=${p.id}`, { withCredentials: true })
            .then(r => r.data.map(s => ({ value: s.id, label: `${p.name} → ${s.name}` })))
            .catch(() => []))
        );
        options = stageArrays.flat();
      } else if (valueType === "pipeline") {
        const res = await axios.get(`${API}/pipelines`, { withCredentials: true });
        options = res.data.map(p => ({ value: p.id, label: p.name }));
      } else if (valueType === "empresa") {
        const res = await axios.get(`${API}/empresas?limit=5000`, { withCredentials: true });
        options = (res.data?.data || res.data).map(e => ({ value: e.id, label: e.nome }));
      } else if (valueType === "contato") {
        const res = await axios.get(`${API}/contatos?limit=5000`, { withCredentials: true });
        options = (res.data?.data || res.data).map(c => ({ value: c.id, label: c.nome }));
      } else if (valueType === "responsavel") {
        const res = await axios.get(`${API}/users/responsaveis`, { withCredentials: true });
        options = res.data.map(r => ({ value: r.user_id, label: r.name }));
      } else if (valueType === "tipo_tarefa") {
        const res = await axios.get(`${API}/task-types`, { withCredentials: true });
        options = res.data.map(tt => ({ value: tt.name, label: tt.name }));
      }
      setDynamicOptions(p => ({ ...p, [valueType]: options }));
    } catch { setDynamicOptions(p => ({ ...p, [valueType]: [] })); }
  };

  // Load trigger module custom fields when trigger module changes
  const handleTriggerModuleChange = async (module) => {
    setForm(f => ({ ...f, trigger: { event: "", module } }));
    if (module && !condCustomFields[module]) {
      try {
        const res = await axios.get(`${API}/custom-fields?module=${module}`, { withCredentials: true });
        setCondCustomFields(p => ({ ...p, [module]: res.data }));
      } catch {}
    }
  };
  const addAction = () => setForm(f => ({ ...f, actions: [...f.actions, { type: "" }] }));
  const updateAction = (i, a) => setForm(f => ({ ...f, actions: f.actions.map((x, j) => j === i ? a : x) }));
  const removeAction = (i) => setForm(f => ({ ...f, actions: f.actions.filter((_, j) => j !== i) }));

  const getTriggerLabel = (auto) => getTriggerFullLabel(auto.trigger?.event, auto.trigger?.module);

  return (
    <div data-testid="automacoes-page" className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: tc50 }}>
            <Zap className="w-5 h-5" style={{ color: tc500 }} strokeWidth={1.5} />
          </div>
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold font-heading text-slate-900 tracking-tight">{t("automacoes.title")}</h1>
            <p className="text-slate-500 mt-0.5">{t("automacoes.subtitle")}</p>
          </div>
        </div>
        <Button onClick={openNew} style={{ backgroundColor: tc500 }} className="text-white hover:opacity-90 h-9 text-sm">
          <Plus className="w-4 h-4 mr-2" />Nova Automação
        </Button>
      </div>

      <div className="space-y-3">
        {loading ? (
          [...Array(3)].map((_, i) => <div key={i} className="h-20 bg-white rounded-lg border border-slate-200 animate-pulse" />)
        ) : automations.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-slate-400 bg-white rounded-lg border border-slate-200">
            <Zap className="w-10 h-10 mb-3 text-slate-300" />
            <p className="text-sm">{t("ui.nenhuma_automacao_criada_ainda")}</p>
            <p className="text-xs mt-1">Clique em t("automacoes.new") para começar.</p>
          </div>
        ) : automations.map(auto => (
          <div key={auto.id} className={`bg-white rounded-lg border shadow-sm p-4 flex items-center gap-4 transition-all ${auto.active ? "border-slate-200" : "border-dashed border-slate-200 opacity-60"}`}>
            <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: auto.active ? `${tc500}18` : "#f1f5f9" }}>
              <Zap className="w-4 h-4" style={{ color: auto.active ? tc500 : "#94a3b8" }} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap mb-1">
                <span className="text-sm font-semibold text-slate-900">{auto.name}</span>
                <Badge className={auto.active ? "bg-emerald-100 text-emerald-700 border-0 text-[10px]" : "bg-slate-100 text-slate-500 border-0 text-[10px]"}>{auto.active ? "Ativa" : "Inativa"}</Badge>
              </div>
              <div className="flex items-center gap-1.5 text-xs text-slate-400 flex-wrap">
                <span className="bg-amber-50 text-amber-700 px-2 py-0.5 rounded font-medium">{getTriggerLabel(auto)}</span>
                <ChevronRight className="w-3 h-3" />
                <span>{(auto.actions || []).length} ação{(auto.actions || []).length !== 1 ? "ões" : ""}</span>
                {(auto.conditions || []).length > 0 && <><span>·</span><span>{auto.conditions.length} condição{auto.conditions.length !== 1 ? "ões" : ""}</span></>}
              </div>
            </div>
            <div className="flex items-center gap-1.5 flex-shrink-0">
              <Switch checked={!!auto.active} onCheckedChange={() => handleToggle(auto)} />
              <Button variant="ghost" size="sm" onClick={() => openLogs(auto)} className="text-slate-400 hover:text-slate-600 h-8 w-8 p-0"><History className="w-3.5 h-3.5" /></Button>
              <Button variant="ghost" size="sm" onClick={() => openEdit(auto)} className="text-sky-600 hover:bg-sky-50 h-8 w-8 p-0"><Pencil className="w-3.5 h-3.5" /></Button>
              <Button variant="ghost" size="sm" onClick={() => setDeleteTarget(auto)} className="text-rose-500 hover:bg-rose-50 h-8 w-8 p-0"><Trash2 className="w-3.5 h-3.5" /></Button>
            </div>
          </div>
        ))}
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="w-[96vw] max-w-5xl h-[92vh] overflow-hidden flex flex-col gap-0 !p-0 border-0 outline-none">
          <DialogHeader className="px-6 pt-6 pb-0">
            <DialogTitle className="font-heading flex items-center gap-2">
              <Zap className="w-5 h-5" style={{ color: tc500 }} />
              {editAuto ? "Editar Automação" : t("automacoes.new")}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
            <div><Label className="text-slate-700">Nome da automação</Label>
              <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Ex: Rotacionar responsável por inatividade" required className="mt-1" /></div>

            {/* TRIGGER */}
            <div className="rounded-xl border border-slate-200 bg-white p-4">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3 flex items-center gap-2">
                <span className="w-5 h-5 rounded bg-amber-100 text-amber-600 flex items-center justify-center text-[10px] font-bold">1</span>
                QUANDO — Trigger
              </p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-slate-500 mb-1 block">{t("ui.modulo")}</Label>
                  <Select value={form.trigger?.module || ""} onValueChange={handleTriggerModuleChange}>
                    <SelectTrigger className="bg-white"><SelectValue placeholder="Selecionar módulo..." /></SelectTrigger>
                    <SelectContent>
                      {MODULES.map(m => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs text-slate-500 mb-1 block">Evento</Label>
                  <Select
                    value={form.trigger?.event || ""}
                    disabled={!form.trigger?.module}
                    onValueChange={v => setForm({ ...form, trigger: { ...form.trigger, event: v } })}>
                    <SelectTrigger className="bg-white"><SelectValue placeholder={form.trigger?.module ? "Selecionar evento..." : "Selecione o módulo primeiro"} /></SelectTrigger>
                    <SelectContent>
                      {(TRIGGERS_BY_MODULE[form.trigger?.module] || []).map(t => (
                        <SelectItem key={t.value} value={optItem.value}>{optItem.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              {form.trigger?.event === "negocio_movido_etapa" && (
                <p className="text-xs text-slate-400 mt-2 bg-amber-50 border border-amber-100 rounded px-3 py-2">
                  <strong>Dica:</strong> O <code className="bg-amber-100 px-1 rounded">estagio_id</code> nas condições refere-se à etapa de <strong>destino</strong> — para onde o card foi movido.
                </p>
              )}
            </div>

            {/* CONDITIONS */}
            <div className="rounded-xl border border-slate-200 bg-white p-4">
              <div className="flex items-center justify-between mb-3">
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider flex items-center gap-2">
                  <span className="w-5 h-5 rounded bg-blue-100 text-blue-600 flex items-center justify-center text-[10px] font-bold">2</span>
                  SE — Condições opcionais
                  {form.conditions.length > 1 && (
                    <span className="text-[10px] font-normal text-slate-400 normal-case tracking-normal">
                      — usando lógica <strong className={form.condition_logic === "OR" ? "text-violet-600" : "text-blue-600"}>{form.condition_logic === "OR" ? "OU" : "E"}</strong>
                    </span>
                  )}
                </p>
                <Button type="button" variant="outline" size="sm" onClick={addCondition} className="h-7 text-xs"><Plus className="w-3 h-3 mr-1" />Adicionar</Button>
              </div>
              {form.conditions.length === 0 && <p className="text-xs text-slate-400 text-center py-2">Sem condições — executa sempre que o trigger ocorrer.</p>}
              <div className="space-y-0">
                {form.conditions.map((cond, i) => {
                  const condMod = cond.module || "negocios";
                  const sysFields = (SYSTEM_FIELDS_BY_MODULE[condMod] || []);
                  const cfFields = (condCustomFields[condMod] || []);
                  const cfFieldsFull = cfFields; // keep full data for type detection
                  return (
                    <>
                    {i > 0 && (
                      <div className="flex items-center gap-2 py-1 px-2">
                        <div className="flex-1 h-px bg-slate-200" />
                        <button
                          type="button"
                          onClick={() => setForm(f => ({ ...f, condition_logic: f.condition_logic === "OR" ? "AND" : "OR" }))}
                          className={`text-[10px] font-bold px-2.5 py-0.5 rounded-full uppercase tracking-wider border transition-all cursor-pointer hover:opacity-80 ${
                            form.condition_logic === "OR"
                              ? "bg-violet-100 text-violet-700 border-violet-300"
                              : "bg-blue-100 text-blue-700 border-blue-300"
                          }`}
                          title="Clique para alternar entre E / OU">
                          {form.condition_logic === "OR" ? "OU" : "E"}
                        </button>
                        <div className="flex-1 h-px bg-slate-200" />
                      </div>
                    )}
                    <div key={i} className="flex items-center gap-2 flex-wrap bg-slate-50 rounded-lg px-2 py-2">
                      {/* Module */}
                      <Select value={condMod} onValueChange={v => { updateCondition(i, { ...cond, module: v, field: "" }); loadCondCustomFields(v); }}>
                        <SelectTrigger className="h-8 text-xs w-28 bg-white flex-shrink-0"><SelectValue placeholder="Módulo..." /></SelectTrigger>
                        <SelectContent>{MODULES.map(m => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}</SelectContent>
                      </Select>
                      {/* Field */}
                      <Select value={cond.field} onValueChange={v => updateCondition(i, { ...cond, field: v })}>
                        <SelectTrigger className="h-8 text-xs w-36 bg-white flex-shrink-0"><SelectValue placeholder="Campo..." /></SelectTrigger>
                        <SelectContent>
                          {sysFields.length > 0 && <>
                            <div className="px-2 py-1 text-[10px] font-bold text-slate-400 uppercase">Sistema</div>
                            {sysFields.map(f => <SelectItem key={f.field_name} value={f.field_name} className="text-xs">{f.field_label}</SelectItem>)}
                          </>}
                          {cfFields.length > 0 && <>
                            <div className="px-2 pt-1 pb-1 text-[10px] font-bold text-slate-400 uppercase border-t mt-1">Personaliz.</div>
                            {cfFields.sort((a, b) => a.field_label.localeCompare(b.field_label, "pt-BR")).map(f => <SelectItem key={f.field_name} value={f.field_name} className="text-xs">{f.field_label}</SelectItem>)}
                          </>}                        </SelectContent>
                      </Select>
                      {/* Operator — filtered based on field type */}
                      {(() => {
                        const allSys = SYSTEM_FIELDS_BY_MODULE[condMod] || [];
                        const selSys = allSys.find(f => f.field_name === cond.field);
                        const cfFull = (condCustomFields[condMod] || []).find(f => f.field_name === cond.field);
                        const isDateF = selSys?.value_type === "date_field" || cfFull?.field_type === "date" || cfFull?.field_type === "datetime";
                        const isSelF = cfFull?.field_type === "select";
                        const isBoolF = cfFull?.field_type === "boolean";
                        const limitedOps = isDateF || isSelF || isBoolF;
                        const ops = limitedOps
                          ? CONDITION_OPERATORS.filter(o => ["eq", "ne"].includes(o.value))
                          : CONDITION_OPERATORS;
                        return (
                          <Select value={cond.operator} onValueChange={v => updateCondition(i, { ...cond, operator: v })}>
                            <SelectTrigger className="h-8 text-xs w-32 bg-white flex-shrink-0"><SelectValue /></SelectTrigger>
                            <SelectContent>{ops.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                          </Select>
                        );
                      })()}
                      {/* Value */}
                      {!["empty", "not_empty"].includes(cond.operator) && (() => {
                          const allSysFields = SYSTEM_FIELDS_BY_MODULE[condMod] || [];
                          const selField = allSysFields.find(f => f.field_name === cond.field);
                          const vType = selField?.value_type;
                          const cfFull = (condCustomFields[condMod] || []).find(f => f.field_name === cond.field);
                          const isDateField = vType === "date_field" || cfFull?.field_type === "date" || cfFull?.field_type === "datetime";
                          if (isDateField) {
                            return <DateValueInput value={cond.value} onChange={v => updateCondition(i, { ...cond, value: v })} />;
                          }
                          // Custom field: select → show options
                          if (cfFull?.field_type === "select" && (cfFull.options || []).length > 0) {
                            return (
                              <Select value={cond.value} onValueChange={v => updateCondition(i, { ...cond, value: v })}>
                                <SelectTrigger className="h-8 text-xs flex-1 min-w-[100px] bg-white"><SelectValue placeholder="Opção..." /></SelectTrigger>
                                <SelectContent>{cfFull.options.map(o => <SelectItem key={o} value={o} className="text-xs">{o}</SelectItem>)}</SelectContent>
                              </Select>
                            );
                          }
                          // Custom field: boolean → Marcado/Não marcado
                          if (cfFull?.field_type === "boolean") {
                            return (
                              <Select value={cond.value} onValueChange={v => updateCondition(i, { ...cond, value: v })}>
                                <SelectTrigger className="h-8 text-xs flex-1 min-w-[100px] bg-white"><SelectValue placeholder="Estado..." /></SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="true" className="text-xs">Marcado (Sim)</SelectItem>
                                  <SelectItem value="false" className="text-xs">Não marcado (Não)</SelectItem>
                                </SelectContent>
                              </Select>
                            );
                          }
                          const staticOpts = vType ? STATIC_VALUE_OPTIONS[vType] : null;
                          const dynOpts = vType && !staticOpts ? dynamicOptions[vType] : null;
                          if (vType && !staticOpts && !dynamicOptions[vType]) loadDynamicOptions(vType);
                          const opts = staticOpts || dynOpts;
                          return opts ? (
                            <Select value={cond.value} onValueChange={v => updateCondition(i, { ...cond, value: v })}>
                              <SelectTrigger className="h-8 text-xs flex-1 min-w-[100px] bg-white"><SelectValue placeholder="Selecionar..." /></SelectTrigger>
                              <SelectContent>{opts.map(o => <SelectItem key={o.value} value={o.value} className="text-xs">{o.label}</SelectItem>)}</SelectContent>
                            </Select>
                          ) : (
                            <Input value={cond.value} onChange={e => updateCondition(i, { ...cond, value: e.target.value })} className="h-8 text-sm flex-1 min-w-[80px] bg-white" />
                          );
                        })()}
                      <Button type="button" variant="ghost" size="sm" onClick={() => removeCondition(i)} className="text-rose-400 hover:bg-rose-50 h-8 w-8 p-0 flex-shrink-0"><X className="w-3.5 h-3.5" /></Button>
                    </div>
                    </>
                  );
                })}
              </div>
            </div>

            {/* ACTIONS */}
            <div className="rounded-xl border border-slate-200 bg-white p-4">
              <div className="flex items-center justify-between mb-3">
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider flex items-center gap-2">
                  <span className="w-5 h-5 rounded bg-emerald-100 text-emerald-600 flex items-center justify-center text-[10px] font-bold">3</span>
                  ENTÃO — Ações
                </p>
                <Button type="button" variant="outline" size="sm" onClick={addAction} className="h-7 text-xs"><Plus className="w-3 h-3 mr-1" />Adicionar</Button>
              </div>
              {form.actions.length === 0 && <p className="text-xs text-slate-400 text-center py-2">Adicione pelo menos uma ação.</p>}
              <div className="space-y-3">
                {form.actions.map((action, i) => (
                  <ActionEditor key={i} action={action} onChange={a => updateAction(i, a)} onRemove={() => removeAction(i)} responsaveis={responsaveis} stages={stages} taskTypes={taskTypes} />
                ))}
              </div>
            </div>

            <div className="flex gap-2 pt-2 pb-2">
              <Button type="button" variant="outline" className="flex-1" onClick={() => setDialogOpen(false)}><X className="w-4 h-4 mr-2" />{t("common.cancel")}</Button>
              <Button type="submit" className="flex-1 text-white hover:opacity-90" style={{ backgroundColor: tc500 }}><Check className="w-4 h-4 mr-2" />{editAuto ? "Salvar" : "Criar Automação"}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Logs Dialog */}
      <Dialog open={logsOpen} onOpenChange={setLogsOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading flex items-center gap-2"><History className="w-5 h-5" style={{ color: tc500 }} />Histórico: {logsTarget?.name}</DialogTitle></DialogHeader>
          {logs.length === 0 ? (
            <div className="text-center py-8 text-slate-400 text-sm">Nenhuma execução registrada ainda.</div>
          ) : (
            <div className="space-y-3">
              {logs.map(log => (
                <div key={log.id} className="bg-slate-50 rounded-lg p-3 border border-slate-200">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-semibold text-slate-700 truncate">{log.record_titulo || log.record_id}</span>
                    <span className="text-[10px] text-slate-400 flex-shrink-0 ml-2">{new Date(log.created_at).toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })}</span>
                  </div>
                  <div className="space-y-1">
                    {(log.actions_results || []).map((r, i) => (
                      <div key={i} className="flex items-center gap-2 text-xs">
                        {r.ok ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0" /> : <AlertCircle className="w-3.5 h-3.5 text-rose-500 flex-shrink-0" />}
                        <span className="font-medium text-slate-600">{r.action}</span>
                        <span className={r.ok ? "text-emerald-600" : "text-rose-500"}>{r.message}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <DeleteConfirmDialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)} title="Excluir automação" description={`${t("common.confirm_delete")} "${deleteTarget?.name}"?`} onConfirm={handleDelete} />
    </div>
  );
}
